import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, Box, Button, LinearProgress, Typography } from "@mui/material";

import { ComparisonCandidate } from "../../api/types/candidate.types";
import { getCandidateDetailsRoute, getProfileRoute } from "../../../../routers/routeUtils";

import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";

const UserDataCell = ({ jobId, candidate }: { jobId: string; candidate: ComparisonCandidate }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "components.compareCandidatesModal" });
  const { fullName, media, club, role, id } = useUserDataFormatter(candidate.candidate);
  const navigate = useNavigate();
  const handleNavigateToDetails = () => navigate(getCandidateDetailsRoute(jobId, candidate.candidateId));
  const profileRoute = getProfileRoute(id!, role!);

  return (
    <Box sx={styles.userDataCell}>
      <Box sx={styles.userInfoContainer}>
        <Avatar src={media} sx={styles.userAvatar}>
          {fullName.substring(0, 2).toUpperCase()}
        </Avatar>
        <Typography sx={styles.userName} title={fullName} component={Link} to={profileRoute}>
          {fullName}
        </Typography>
        <Typography sx={styles.userRoleAndClub}>{role}</Typography>
        {club && <Typography sx={styles.userRoleAndClub}>{club}</Typography>}
      </Box>
      <Box sx={styles.userGinoScoreContainer}>
        <LinearProgress variant="determinate" value={candidate.fitScore} sx={styles.userGinoScoreProgress} />
        <Typography sx={styles.userGinoScore}>{candidate.fitScore}%</Typography>
      </Box>
      <Button fullWidth variant="outlined-sm" sx={styles.userViewDetailsButton} onClick={handleNavigateToDetails}>
        {t("viewDetails")}
      </Button>
    </Box>
  );
};

export default UserDataCell;

const styles = {
  userDataCell: {
    width: "164px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
  },
  userInfoContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid var(--Gray---100, #E7E7E7)",
    background: "#FFF",
    padding: "20px",
  },
  userAvatar: {
    width: "50px",
    height: "50px",
  },
  userName: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    lineClamp: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "90%",
    textDecoration: "none",
  },
  userRoleAndClub: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
  },
  userGinoScore: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "normal",
  },
  userGinoScoreProgress: {
    width: "108px",
    height: "8px",
    borderRadius: "4px",
  },
  userGinoScoreContainer: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
  userViewDetailsButton: {
    color: "var(--Gray-700, #344054) !important",
    border: "1px solid var(--Gray-300, #D0D5DD) !important",
    background: "var(--Base-White, #FFF) !important",
  },
};
